/*
Copyright (c) 2025 Mathilde Meyer, Naziha Beghdadi
Licensed under the MIT License (see LICENSE.txt file for details)
*/

import React, { useState } from "react";
import Radio from "@mui/material/Radio";
import RadioGroup from "@mui/material/RadioGroup";
import FormControlLabel from "@mui/material/FormControlLabel";
import FormControl from "@mui/material/FormControl";
import FormLabel from "@mui/material/FormLabel";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import TextField from "@mui/material/TextField";
import { Box, Typography } from "@mui/material";

const DIGIT_ONLY_PROPS = {
  onKeyDown: (e) => {
    const allowed = ["Backspace", "Tab", "ArrowLeft", "ArrowRight", "Delete"];
    if (!/[0-9]/.test(e.key) && !allowed.includes(e.key)) e.preventDefault();
  },
  onPaste: (e) => {
    if (!/^\d+$/.test(e.clipboardData.getData("text"))) e.preventDefault();
  },
};

function Repetition({ index, data, setData, indexIntervention }) {
  const [errorStartDay, setErrorStartDay] = useState("");

  const handleChange = (e) => {
    const newData = [...data];
    const currentInterventions = [...newData[index].interventions];
    currentInterventions[indexIntervention] = {
      ...currentInterventions[indexIntervention],
      [e.target.name]: e.target.value,
    };

    if (e.target.name === "lastIntervention") {
      if (e.target.value === "no") {
        const isLast = indexIntervention === currentInterventions.length - 1;
        if (isLast) {
          currentInterventions.push({ lastIntervention: "yes" });
        }
      }
      if (e.target.value === "yes") {
        const isLast = indexIntervention === currentInterventions.length - 2;
        const lastItem = currentInterventions[currentInterventions.length - 1];
        if (isLast && Object.keys(lastItem).length <= 1) {
          currentInterventions.pop();
        }
      }
    }

    if (e.target.name === "daysStart") {
      const startDay = parseInt(e.target.value, 10);
      const acclimatation = newData[index].acclimatation;
      const acclimatationDays = parseInt(newData[index].acclimatationDays || "0");
      const preparationDays = parseInt(newData[index].preparationDays || "0");

      let acclimatationEndDay = 0;
      if (acclimatation === "yes" && acclimatationDays > 0) {
        acclimatationEndDay = acclimatationDays;
      }

      const preparationStartDay = acclimatationEndDay > 0 ? acclimatationEndDay + 1 : 1;
      const preparationEndDay = preparationStartDay + (preparationDays > 0 ? preparationDays : 0) - 1;
      const minStartDay = Math.max(acclimatationEndDay, preparationEndDay);

      if (!isNaN(startDay) && startDay <= minStartDay) {
        setErrorStartDay(`Intervention must start after day ${minStartDay}.`);
      } else {
        setErrorStartDay("");
      }
    }

    newData[index] = { ...newData[index], interventions: currentInterventions };
    setData(newData);
  };

  const intervention = data[index].interventions[indexIntervention];
  const repeatType = intervention.repeatedIntervention || "";
  const isSpecificDays = repeatType === "specific_days";

  return (
    <Box>
      {/* 1. Start day (hidden for specific_days — user enters all days directly) */}
      {!isSpecificDays && (
        <TextField
          id="daysStart"
          name="daysStart"
          label="On which day does this intervention start?"
          variant="outlined"
          fullWidth
          sx={{ mb: 2 }}
          value={intervention.daysStart || ""}
          onChange={handleChange}
          error={!!errorStartDay}
          helperText={errorStartDay || "Day number relative to the start of the experiment (e.g. 8)."}
          {...DIGIT_ONLY_PROPS}
        />
      )}

      {/* 2. Repetition schedule */}
      <FormControl fullWidth sx={{ mb: 2 }}>
        <InputLabel id="repeatedInterventionLabel">How often is this intervention performed?</InputLabel>
        <Select
          labelId="repeatedInterventionLabel"
          id="repeatedIntervention"
          name="repeatedIntervention"
          label="How often is this intervention performed?"
          value={repeatType}
          onChange={handleChange}
        >
          <MenuItem value="no">Once — not repeated</MenuItem>
          <MenuItem value="every_day">Daily — every day for X consecutive days</MenuItem>
          <MenuItem value="every_x_days">Periodic — every X days, Y times total</MenuItem>
          <MenuItem value="after_x_days">Delayed — once, then again X days later</MenuItem>
          <MenuItem value="specific_days">Custom — on specific days (e.g. 10, 12, 16)</MenuItem>
        </Select>
      </FormControl>

      {/* 3. Extra fields based on repetition mode */}

      {isSpecificDays && (
        <Box sx={{ mb: 2, p: 2, border: "1px solid #e0e0e0", borderRadius: 1.5, backgroundColor: "#f9fbfc" }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1, fontSize: "0.82rem" }}>
            Enter each day number separated by a comma. These are the exact experiment days on which the
            intervention is performed.
          </Typography>
          <TextField
            name="specificDays"
            label="Intervention days (comma-separated)"
            placeholder="e.g. 10, 12, 16, 20"
            variant="outlined"
            fullWidth
            value={intervention.specificDays || ""}
            onChange={handleChange}
            inputProps={{ inputMode: "text" }}
            helperText="Example: 10, 12, 16 means the intervention happens on day 10, day 12, and day 16."
          />
        </Box>
      )}

      {(repeatType === "every_day") && (
        <TextField
          name="xDays"
          label="Number of consecutive days"
          placeholder="e.g. 5"
          helperText={`Intervention runs daily starting on day ${intervention.daysStart || "?"} for this many days.`}
          variant="outlined"
          fullWidth
          sx={{ mb: 2 }}
          value={intervention.xDays || ""}
          onChange={handleChange}
          {...DIGIT_ONLY_PROPS}
        />
      )}

      {repeatType === "every_x_days" && (
        <>
          <TextField
            name="xDays"
            label="Interval between occurrences (days)"
            placeholder="e.g. 3"
            helperText="How many days between each occurrence."
            variant="outlined"
            fullWidth
            sx={{ mb: 2 }}
            value={intervention.xDays || ""}
            onChange={handleChange}
            {...DIGIT_ONLY_PROPS}
          />
          <TextField
            name="yTimes"
            label="Total number of occurrences"
            placeholder="e.g. 4"
            helperText={`First occurrence on day ${intervention.daysStart || "?"}, then every ${intervention.xDays || "X"} days.`}
            variant="outlined"
            fullWidth
            sx={{ mb: 2 }}
            value={intervention.yTimes || ""}
            onChange={handleChange}
            {...DIGIT_ONLY_PROPS}
          />
        </>
      )}

      {repeatType === "after_x_days" && (
        <TextField
          name="xDays"
          label="Days until the repeat"
          placeholder="e.g. 7"
          helperText={`First on day ${intervention.daysStart || "?"}, repeated once ${intervention.xDays || "X"} days later.`}
          variant="outlined"
          fullWidth
          sx={{ mb: 2 }}
          value={intervention.xDays || ""}
          onChange={handleChange}
          {...DIGIT_ONLY_PROPS}
        />
      )}

      {/* 4. Is this the last intervention? */}
      <FormControl sx={{ mt: 1 }}>
        <FormLabel id="lastInterventionLabel">Is this the last intervention?</FormLabel>
        <RadioGroup
          aria-labelledby="lastInterventionLabel"
          name="lastIntervention"
          value={intervention.lastIntervention || "yes"}
          onChange={handleChange}
        >
          <FormControlLabel value="yes" control={<Radio />} label="Yes" />
          <FormControlLabel value="no" control={<Radio />} label="No" />
        </RadioGroup>
      </FormControl>
    </Box>
  );
}

export default Repetition;
